import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import cn from "classnames";
import styles from "./Settings.module.sass";
import ContractGeneralInfo from "./TutorInfo";
import Confirmation from "./Confirmation";
import { getFolderById, updateFolder } from "../../services/folder.service";
import { createTutor } from "../../services/tutor.service";
import { toast } from "react-toastify";


const CompanyCheckInformation3 = () => {
  const { id: folderId } = useParams(); // Récupérer l'ID du folder depuis l'URL

  // États pour la navigation et les données
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [folderData, setFolderData] = useState({});
  const [contractData, setContractData] = useState({
    generalInfo: {}
  });
  const [contractId, setContractId] = useState(null);
  const [isStepValid, setIsStepValid] = useState(false);

  // Effet pour synchroniser les données des tuteurs après chaque étape
  useEffect(() => {
    if (folderData.tutor_id && folderData.tutor_id.length > 0) {
      syncTutorData();
    }
  }, [folderData.tutor_id, currentStep]);

  // Configuration des étapes pour l'ajout de tuteurs
  const steps = [
    {
      id: 0,
      title: "Ajouter des tuteurs",
      component: ContractGeneralInfo,
    },
    {
      id: 1,
      title: "Confirmation",
      component: Confirmation,
    }
  ];


  // Récupérer les informations du dossier au chargement du composant
  useEffect(() => {
    const fetchFolderData = async () => {
      if (!folderId) {
        setError("ID du dossier manquant");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const folder = await getFolderById(folderId);
        setFolderData(folder);
        
        // Si le dossier a déjà des tuteurs associés, récupérer leurs informations
        if (folder.tutor_id && folder.tutor_id.length > 0) {
          // Initialiser les données avec les tuteurs existants
          const tutorData = folder.expand?.tutor_id?.[0] || {};
          const secondTutorData = folder.expand?.tutor_id?.[1] || {};
          
          setContractData({
            generalInfo: {
              tutor: tutorData,
              second_tutor: secondTutorData,
              has_second_tutor: folder.tutor_id.length > 1 ? 'Oui' : 'Non'
            }
          });
          
          // Si des tuteurs existent, aller directement à la confirmation
          setCurrentStep(1);
          toast.info('Tuteurs existants chargés');
        }
        
        setError(null);
      } catch (error) {
        console.error('Erreur lors de la récupération des données du dossier:', error);
        setError("Impossible de récupérer les informations du dossier");
      } finally {
        setLoading(false);
      }
    };

    fetchFolderData();
  }, [folderId]);


  // Navigation entre les étapes
  const goToNextStep = async () => {
    if (currentStep < steps.length - 1) {
      setSaving(true);
      try {
        // Si on passe de l'étape 0 (Ajouter des tuteurs) à l'étape 1 (Confirmation)
        if (currentStep === 0 && contractData.generalInfo && Object.keys(contractData.generalInfo).length > 0) {
          try {
            // Récupérer les données du dossier pour obtenir company_id
            const folder = await getFolderById(folderId);
            const companyId = folder.company_id;
            
            const tutorsIds = [];
            
            // Gérer le tuteur principal
            if (contractData.generalInfo.tutor_id) {
              // Tuteur existant sélectionné via recherche - juste l'associer
              tutorsIds.push(contractData.generalInfo.tutor_id);
              console.log('Tuteur principal existant associé:', contractData.generalInfo.tutor_id);
            } else if (contractData.generalInfo.tutor && 
                       (contractData.generalInfo.tutor.last_name || contractData.generalInfo.tutor.first_name)) {
              // Nouveau tuteur à créer via formulaire
              const tutorPayload = {
                ...contractData.generalInfo.tutor,
                company_id: companyId,
                folder_id: folderId
              };
              
              const createdTutor = await createTutor(tutorPayload);
              tutorsIds.push(createdTutor.id);
              console.log('Nouveau tuteur principal créé:', createdTutor.id);
            }
            
            // Gérer le second tuteur SEULEMENT si l'utilisateur a choisi "Oui"
            if (contractData.generalInfo.has_second_tutor === 'Oui') {
              if (contractData.generalInfo.second_tutor_id) {
                // Second tuteur existant sélectionné via recherche - juste l'associer
                tutorsIds.push(contractData.generalInfo.second_tutor_id);
                console.log('Second tuteur existant associé:', contractData.generalInfo.second_tutor_id);
              } else if (contractData.generalInfo.second_tutor &&
                         (contractData.generalInfo.second_tutor.last_name || contractData.generalInfo.second_tutor.first_name)) {
                // Nouveau second tuteur à créer via formulaire
                const secondTutorPayload = {
                  ...contractData.generalInfo.second_tutor,
                  company_id: companyId,
                  folder_id: folderId
                };
                
                const createdSecondTutor = await createTutor(secondTutorPayload);
                tutorsIds.push(createdSecondTutor.id);
                console.log('Nouveau second tuteur créé:', createdSecondTutor.id);
              }
            }
            
            // Récupérer les données actuelles du dossier pour la progression
            const currentFolder = await getFolderById(folderId);
            const currentProgression = currentFolder.progression || {};
            
            // Mettre à jour la progression : tuteur assigné
            const updatedProgression = {
              ...currentProgression,
              entreprise: {
                ...currentProgression.entreprise,
                tuteur_assigne: true
              }
            };
            
            console.log('IDs des tuteurs à associer au dossier:', tutorsIds);
            
            // Mettre à jour le dossier avec les IDs des tuteurs et la progression
            await updateFolder(folderId, {
              tutor_id: tutorsIds, // Relation multiple pour les tuteurs (nom correct du champ)
              progression: updatedProgression
            });
            
            console.log('Tuteurs associés avec succès au dossier');
            toast.success(`${tutorsIds.length} tuteur(s) associé(s) avec succès`);
            
          } catch (error) {
            console.error('Erreur lors de la sauvegarde des tuteurs:', error);
            toast.error('Erreur lors de la sauvegarde des tuteurs');
          }
        }

        setCurrentStep(currentStep + 1);
      } finally {
        setSaving(false);
      }
    }
  };

  const goToPreviousStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const goToStep = (stepIndex) => {
    if (stepIndex >= 0 && stepIndex < steps.length) {
      setCurrentStep(stepIndex);
    }
  };

  // Mise à jour des données des tuteurs
  const updateStepData = (stepKey, data) => {
    console.log('updateStepData appelé avec:', stepKey, data);
    
    // Gérer la validation des étapes
    if (stepKey === 'isValid') {
      setIsStepValid(data);
      return;
    }
    
    setContractData(prev => {
      const newData = {
        ...prev,
        [stepKey]: typeof data === 'object' && data !== null ? { ...prev[stepKey], ...data } : data
      };
      console.log('Nouvelles données contractData:', newData);
      return newData;
    });
  };
  
  // Fonction pour synchroniser les données des tuteurs avec le backend
  const syncTutorData = async () => {
    if (folderData.tutor_id && folderData.tutor_id.length > 0) {
      try {
        const updatedFolder = await getFolderById(folderId);
        if (updatedFolder && updatedFolder.expand?.tutor_id) {
          // Mettre à jour les données des tuteurs dans l'état local
          const tutorData = updatedFolder.expand.tutor_id[0] || {};
          const secondTutorData = updatedFolder.expand.tutor_id[1] || {};
          
          setContractData(prev => ({
            ...prev,
            generalInfo: {
              ...prev.generalInfo,
              tutor: tutorData,
              second_tutor: secondTutorData,
              has_second_tutor: updatedFolder.tutor_id.length > 1 ? 'Oui' : 'Non'
            }
          }));
        }
      } catch (error) {
        console.error('Erreur lors de la récupération des détails des tuteurs:', error);
      }
    }
  };

  // Rendu du composant de l'étape actuelle
  const CurrentStepComponent = steps[currentStep].component;

  // Affichage de chargement
  if (loading) {
    return (
      <div className={styles.settings}>
        <div className={styles.wrapper}>
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <p>Chargement des informations du dossier...</p>
          </div>
        </div>
      </div>
    );
  }

  // Affichage d'erreur
  if (error) {
    return (
      <div className={styles.settings}>
        <div className={styles.wrapper}>
          <div style={{ textAlign: 'center', padding: '2rem', color: 'red' }}>
            <p>Erreur : {error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.settings}>
      {/* Navigation par étapes */}
      <div className={styles.menu}>
        {steps.map((step, index) => (
          <button
            className={cn(styles.button, {
              [styles.active]: currentStep === index,
              [styles.completed]: currentStep > index,
            })}
            key={step.id}
          >
            <span className={styles.stepNumber}>{index + 1}</span>
            {step.title}
          </button>
        ))}
      </div>

      {/* Contenu de l'étape actuelle */}
      <div className={styles.wrapper}>
        <div className={styles.stepContent}>
          <div className={styles.componentWrapper}>
            <CurrentStepComponent
              data={{ folder: folderData, contract: contractData }}
              updateData={updateStepData}
              currentStep={currentStep}
              folderId={folderId}
            />
          </div>
        </div>

        {/* Boutons de navigation */}
        {currentStep < steps.length - 1 && (
          <div className={styles.navigationButtons}>
            <button
              className="button-stroke"
              onClick={goToPreviousStep}
              disabled={currentStep === 0 || saving}
            >
              Précédent
            </button>

            <button
              className="button"
              onClick={goToNextStep}
              disabled={saving || !isStepValid}
            >
              {saving ? "Sauvegarde..." : "Suivant"}
            </button>
          </div>
        )}

      </div>
    </div>
  );
};

export default CompanyCheckInformation3;
